import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import '../styles/ProductDetails.css'

const bankOfGeorgia = `${window.location.origin}/bog-logo.svg`
const BASE_URL = 'https://api.yourhood.ge'

const DELIVERY_OPTIONS = [
  { id: 'tbilisi', label: 'მიწოდება თბილისში',       price: 6,  type: 0 },
  { id: 'city',    label: 'მიწოდება სხვა ქალაქებში', price: 8,  type: 1 },
  { id: 'region',  label: 'მიწოდება რეგიონებში',     price: 13, type: 2 },
]

const EXCLUDE_BODY_CATEGORIES = ['ჩანთა', 'ჩანთები', 'ფეხსაცმელი']

const needsBodyInfo = (categoryName) => {
  if (!categoryName) return false
  const lower = categoryName.toLowerCase()
  return !EXCLUDE_BODY_CATEGORIES.some(c => lower.includes(c.toLowerCase()))
}

export default function ProductDetails({ addToCart }) {
  const { id } = useParams()
  const [product, setProduct]           = useState(null)
  const [loading, setLoading]           = useState(true)
  const [selectedSize, setSelectedSize] = useState(null)
  const [selectedColor, setSelectedColor] = useState(null)
  const [mainImage, setMainImage]       = useState(0)
  const [added, setAdded]               = useState(false)
  const [showForm, setShowForm]         = useState(false)
  const [form, setForm]                 = useState({ fullName: '', phone: '', address: '', bodyInfo: '' })
  const [formErrors, setFormErrors]     = useState({})
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [apiError, setApiError]         = useState('')
  const [delivery, setDelivery]         = useState(null)

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    setLoading(true)
    setMainImage(0)
    setSelectedSize(null)
    setSelectedColor(null)
    fetch(`${BASE_URL}/api/Product/protudct-details?ProductID=${id}`, {
      headers: { 'X-Language': 'ka' }
    })
      .then(res => res.json())
      .then(data => { setProduct(data); setLoading(false) })
      .catch(err => { console.error('Product details error:', err); setLoading(false) })
  }, [id])

  const activePrice = product?.salePrice ?? product?.price
  const sizes       = product?.availableSizes?.length > 0 ? product.availableSizes : []
  const colors      = product?.colors?.length > 0 ? product.colors : []

  const handleAddToBag = () => {
    if (!product) return
    const errors = {}
    if (sizes.length > 0 && !selectedSize)   errors.size  = 'აირჩიეთ ზომა'
    if (colors.length > 0 && !selectedColor) errors.color = 'აირჩიეთ ფერი'
    if (Object.keys(errors).length > 0) { setFormErrors(prev => ({ ...prev, ...errors })); return }
    addToCart({
      id: product.id,
      name: product.title,
      category: product.categoryName,
      price: activePrice,
      originalPrice: product.price,
      image: product.imageUrls?.[0] || `https://picsum.photos/seed/${product.id}/600/800`,
      size: selectedSize,
      color: selectedColor || undefined,
      availableSizes: sizes,
      availableColors: colors,
    })
    setAdded(true)
    setTimeout(() => setAdded(false), 2000)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    setFormErrors(prev => ({ ...prev, [name]: '' }))
    setApiError('')
  }

  const validate = () => {
    const errors = {}
    if (!form.fullName.trim()) errors.fullName = 'სახელი სავალდებულოა'
    if (!form.phone.trim()) {
      errors.phone = 'ტელეფონი სავალდებულოა'
    } else if (!/^5\d{8}$/.test(form.phone.replace(/\s/g, ''))) {
      errors.phone = 'შეიყვანეთ სწორი ნომერი (მაგ: 555112233)'
    }
    if (!form.address.trim())  errors.address  = 'მისამართი სავალდებულოა'
    if (sizes.length > 0 && !selectedSize)   errors.size  = 'აირჩიეთ ზომა'
    if (colors.length > 0 && !selectedColor) errors.color = 'აირჩიეთ ფერი'
    if (!delivery) errors.delivery = 'მიწოდების ტიპი სავალდებულოა'
    return errors
  }

  const handleBogPayment = async () => {
    const errors = validate()
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }

    const selectedDelivery = DELIVERY_OPTIONS.find(o => o.id === delivery)
    setPaymentLoading(true)
    setApiError('')

    try {
      const response = await fetch(`${BASE_URL}/Payment/BOG/proceed-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Language': 'ka', 'accept': '*/*' },
        body: JSON.stringify({
          fullName: form.fullName,
          phone: form.phone,
          address: form.address,
          comment: form.bodyInfo || ' ',
          deliveryType: selectedDelivery.type,
          items: [{ productID: product.id, quantity: 1, size: selectedSize || '', color: selectedColor || '' }],
        }),
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.message || 'გადახდა ვერ მოხერხდა')
      const redirectUrl = data?._links?.redirect?.href
      if (redirectUrl) window.location.href = redirectUrl
    } catch (err) {
      setApiError(err.message || 'დაფიქსირდა შეცდომა. სცადეთ თავიდან.')
    } finally {
      setPaymentLoading(false)
    }
  }

  const handleBogInstallment = () => {
    const errors = validate()
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return }

    const selectedDelivery = DELIVERY_OPTIONS.find(o => o.id === delivery)
    const totalAmount = activePrice + selectedDelivery.price

    if (!totalAmount || isNaN(totalAmount)) { setApiError('თანხის გამოთვლა ვერ მოხერხდა'); return }
    if (!window.BOG?.Calculator) { setApiError('განვადების სერვისი ვერ ჩაიტვირთა. გადატვირთეთ გვერდი.'); return }

    window.BOG.Calculator.open({
      amount: totalAmount,
      onClose: () => {},
      onRequest: (selected, successCb, closeCb) => {
        console.log('BOG installment selected:', selected)
        setPaymentLoading(true)
        setApiError('')
        fetch(`${BASE_URL}/Payment/BOG/proceed-installment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Language': 'ka', 'accept': '*/*' },
          body: JSON.stringify({
            fullName: form.fullName,
            phone: form.phone,
            address: form.address,
            comment: form.bodyInfo || ' ',
            month: selected.month,
            installmenttype: selected.discount_code,
            deliveryType: selectedDelivery.type,
            items: [{ productID: product.id, quantity: 1, size: selectedSize || '', color: selectedColor || '' }],
          }),
        })
          .then(res => res.json())
          .then(data => {
            console.log('BOG installment response:', data)
            if (data.order_id) {
              const targetLink = data.links?.find(l => l.rel === 'target')
              successCb(data.order_id)
              if (targetLink?.href) window.location.href = targetLink.href
            } else {
              closeCb()
              setApiError(data.message || 'განვადება ვერ მოხერხდა')
            }
          })
          .catch(() => {
            closeCb()
            setApiError('დაფიქსირდა შეცდომა. სცადეთ თავიდან.')
          })
          .finally(() => setPaymentLoading(false))
      },
      onComplete: () => {},
    })
  }

  if (loading) return <div className="pd-page"><div className="pd-loading">იტვირთება...</div></div>
  if (!product) return <div className="pd-page"><div className="pd-loading">პროდუქტი ვერ მოიძებნა</div></div>

  const images = product.imageUrls?.length > 0
    ? product.imageUrls
    : [`https://picsum.photos/seed/${product.id}/600/800`]

  const deliveryPrice = delivery ? (DELIVERY_OPTIONS.find(o => o.id === delivery)?.price ?? 0) : 0
  const totalPrice    = activePrice + deliveryPrice

  const isFormValid = (
    form.fullName.trim() &&
    form.phone.trim() &&
    form.address.trim() &&
    (sizes.length === 0 || selectedSize) &&
    (colors.length === 0 || selectedColor) &&
    delivery
  )

  const showBodyInfo = needsBodyInfo(product.categoryName)

  return (
    <div className="pd-page">
      <nav className="pd-breadcrumb">
        <span>მთავარი</span>
        <span className="pd-breadcrumb-sep">—</span>
        <span>{product.categoryName}</span>
        <span className="pd-breadcrumb-sep">—</span>
        <span className="pd-breadcrumb-active">{product.title}</span>
      </nav>

      <div className="pd-body">
        {/* ── Left: Images ── */}
        <div className="pd-gallery">
          <div className="pd-thumbnails">
            {images.map((url, i) => (
              <div
                key={i}
                className={`pd-thumb ${mainImage === i ? 'pd-thumb--active' : ''}`}
                onClick={() => setMainImage(i)}
              >
                <img src={url} alt={`ხედი ${i + 1}`} />
              </div>
            ))}
          </div>
          <div className="pd-main-image">
            <img src={images[mainImage]} alt={product.title} />
            {product.salePrice != null && <span className="pd-sale-badge">SALE</span>}
          </div>
        </div>

        {/* ── Right: Info ── */}
        <div className="pd-info">
          <span className="pd-category">{product.categoryName}</span>
          <h1 className="pd-name">{product.title}</h1>

          <div className="pd-price-row">
            {product.salePrice != null ? (
              <>
                <span className="pd-price pd-price--sale">₾{product.salePrice}</span>
                <span className="pd-price pd-price--original">₾{product.price}</span>
              </>
            ) : (
              <span className="pd-price">₾{product.price}</span>
            )}
          </div>

          {sizes.length > 0 && (
            <div className="pd-size-section">
              <div className="pd-size-header">
                <span className={`pd-size-label ${formErrors.size ? 'pd-size-label--error' : ''}`}>ზომა</span>
                {formErrors.size && <span className="pd-field-error">{formErrors.size}</span>}
              </div>
              <div className={`pd-sizes ${formErrors.size ? 'pd-sizes--error' : ''}`}>
                {sizes.map(size => (
                  <button
                    key={size}
                    className={`pd-size-btn ${selectedSize === size ? 'pd-size-btn--active' : ''}`}
                    onClick={() => { setSelectedSize(size); setFormErrors(prev => ({ ...prev, size: '' })) }}
                  >{size}</button>
                ))}
              </div>
            </div>
          )}

          {colors.length > 0 && (
            <div className="pd-color-section">
              <div className="pd-color-header">
                <span className={`pd-size-label ${formErrors.color ? 'pd-size-label--error' : ''}`}>ფერი</span>
                {formErrors.color && <span className="pd-field-error">{formErrors.color}</span>}
              </div>
              <div className={`pd-sizes ${formErrors.color ? 'pd-sizes--error' : ''}`}>
                {colors.map(color => (
                  <button
                    key={color}
                    className={`pd-size-btn ${selectedColor === color ? 'pd-size-btn--active' : ''}`}
                    onClick={() => { setSelectedColor(color); setFormErrors(prev => ({ ...prev, color: '' })) }}
                  >{color}</button>
                ))}
              </div>
            </div>
          )}

          <div className="pd-actions">
            <button className="pd-add-btn" onClick={handleAddToBag}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <path d="M16 10a4 4 0 01-8 0"/>
              </svg>
              {added ? 'დამატებულია' : 'კალათაში დამატება'}
            </button>
            <button
              className={`pd-buy-btn ${showForm ? 'pd-buy-btn--active' : ''}`}
              onClick={() => setShowForm(prev => !prev)}
            >ყიდვა</button>
          </div>

          <div className={`pd-order-form ${showForm ? 'pd-order-form--open' : ''}`}>
            <div className="pd-order-form-inner">
              <p className="pd-form-label">მიწოდების ინფორმაცია</p>

              <div className="pd-field">
                <input className={`pd-input ${formErrors.fullName ? 'pd-input--error' : ''}`} type="text" name="fullName" placeholder="სახელი და გვარი" value={form.fullName} onChange={handleChange} />
                {formErrors.fullName && <span className="pd-field-error">{formErrors.fullName}</span>}
              </div>
              <div className="pd-field">
                <input className={`pd-input ${formErrors.phone ? 'pd-input--error' : ''}`} type="tel" name="phone" placeholder="ტელეფონის ნომერი" value={form.phone} onChange={handleChange} />
                {formErrors.phone && <span className="pd-field-error">{formErrors.phone}</span>}
              </div>
              <div className="pd-field">
                <input className={`pd-input ${formErrors.address ? 'pd-input--error' : ''}`} type="text" name="address" placeholder="მისამართი" value={form.address} onChange={handleChange} />
                {formErrors.address && <span className="pd-field-error">{formErrors.address}</span>}
              </div>

              {showBodyInfo && (
                <div className="pd-field">
                  <p className="pd-body-info-hint">მეტი სიზუსტისთვის მიუთითეთ თქვენი სიმაღლე და წონა</p>
                  <input className="pd-input" type="text" name="bodyInfo" placeholder="მაგ: 170სმ / 65კგ" value={form.bodyInfo} onChange={handleChange} />
                </div>
              )}

              <div className="pd-delivery-section">
                <p className="pd-delivery-title">მიწოდება</p>
                <div className="pd-delivery-options">
                  {DELIVERY_OPTIONS.map(opt => (
                    <label
                      key={opt.id}
                      className={`pd-delivery-option ${delivery === opt.id ? 'pd-delivery-option--active' : ''}`}
                      onClick={() => { setDelivery(opt.id); setFormErrors(prev => ({ ...prev, delivery: '' })) }}
                    >
                      <span className={`pd-radio ${delivery === opt.id ? 'pd-radio--checked' : ''}`}>
                        {delivery === opt.id && <span className="pd-radio-dot" />}
                      </span>
                      <span className="pd-delivery-label">{opt.label}</span>
                      <span className="pd-delivery-price">₾{opt.price}</span>
                    </label>
                  ))}
                </div>
                {formErrors.delivery && <span className="pd-field-error">{formErrors.delivery}</span>}
              </div>

              {delivery && (
                <div className="pd-total-row">
                  <span className="pd-total-label">სულ გადასახდელი</span>
                  <span className="pd-total-price">₾{totalPrice}</span>
                </div>
              )}

              {apiError && <div className="pd-api-error">{apiError}</div>}

              <div className="pd-bog-buttons">
                <button
                  className={`pd-bog-btn ${!isFormValid ? 'pd-bog-btn--locked' : ''}`}
                  disabled={!isFormValid || paymentLoading}
                  onClick={handleBogPayment}
                >
                  {paymentLoading ? <span className="pd-spinner" /> : (
                    <>
                      <img src={bankOfGeorgia} alt="საქართველოს ბანკი" className="pd-bog-logo" />
                      <span>საქართველოს ბანკი</span>
                    </>
                  )}
                </button>

                <button
                  className={`pd-bog-installment-btn ${!isFormValid ? 'pd-bog-installment-btn--locked' : ''}`}
                  disabled={!isFormValid || paymentLoading}
                  onClick={handleBogInstallment}
                >
                  {paymentLoading ? <span className="pd-spinner" /> : (
                    <>
                      <img src={bankOfGeorgia} alt="საქართველოს ბანკი" className="pd-bog-logo" />
                      <span>განვადება</span>
                    </>
                  )}
                </button>

                <button
                  className={`pd-tbc-btn ${!isFormValid ? 'pd-tbc-btn--locked' : ''}`}
                  disabled={!isFormValid || paymentLoading}
                  onClick={handleBogPayment}
                >
                  {paymentLoading ? <span className="pd-spinner pd-spinner--white" /> : (
                    <span>ტოპ | <span style={{ fontWeight: 500 }}>ქარდი</span></span>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="pd-description">
            <div className="pd-desc-toggle">
              <span>აღწერა</span>
              <span className="pd-desc-line" />
            </div>
            <p className="pd-desc-text">
              {product.description || 'პრემიუმ ხარისხის პროდუქტი ჩვენი უახლესი კოლექციიდან.'}
            </p>
          </div>

          <div className="pd-details">
            <div className="pd-desc-toggle">
              <span>დეტალები</span>
              <span className="pd-desc-line" />
            </div>
            <ul className="pd-details-list">
              <li>კატეგორია: {product.categoryName}</li>
              <li>მაღალი ხარისხის მასალა</li>
            </ul>
            <div className="pd-delivery-info">
              <p className="pd-delivery-info-title">მიწოდების ვადები</p>
              <ul className="pd-details-list">
                <li>თბილისი: 2–4 სამუშაო დღე</li>
                <li>რეგიონი: 3–5 სამუშაო დღე</li>
              </ul>
              <p className="pd-delivery-info-note">
                გარდა იმ ნივთებისა, რომელიც ჩამოდის შეკვეთით 2–3 კვირის ვადაში.
                აღნიშნული ინფორმაცია მითითებულია კონკრეტულ ნივთებზე უშუალოდ ყიდვის მომენტში და ასევე აღწერაში.
                გთხოვთ ყურადღებით დააკვირდეთ.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
